@if (visible()) {
  <div [formGroup]="group()" class="flex flex-col gap-xs">
    @switch (param().type) {
      @case (ParamType.FIELD_KEY) {
        @if (fieldOptions().length) {
          <lib-select
            [inputFormControlName]="param().key"
            [id]="controlId()"
            [name]="param().key"
            [label]="label()"
            [required]="param().required"
            [options]="fieldOptions()"
            [selectedKeyName]="'label'"
            [selectedKeyValue]="'value'"
            [hasSearch]="true"
            [searchPlaceholder]="'type_to_search_placeholder' | transloco" />
        } @else {
          <ng-container *ngTemplateOutlet="noOptions" />
        }
      }

      @case (ParamType.FIELD_KEY_LIST) {
        @if (fieldOptions().length) {
          <lib-multi-select
            [inputFormControlName]="param().key"
            [id]="controlId()"
            [name]="param().key"
            [label]="label()"
            [required]="param().required"
            [options]="fieldOptions()"
            [selectedKeyName]="'label'"
            [selectedKeyValue]="'value'" />
        } @else {
          <ng-container *ngTemplateOutlet="noOptions" />
        }
      }

      @case (ParamType.ENUM) {
        @if (enumOptions().length) {
          <lib-select
            [inputFormControlName]="param().key"
            [id]="controlId()"
            [name]="param().key"
            [label]="label()"
            [required]="param().required"
            [options]="enumOptions()"
            [selectedKeyName]="'label'"
            [selectedKeyValue]="'value'" />
        } @else {
          <ng-container *ngTemplateOutlet="noOptions" />
        }
      }

      @case (ParamType.MULTI_ENUM) {
        @if (checkboxOptions().length) {
          <lib-checkbox
            [id]="controlId()"
            [formGroup]="group()"
            [inputFormControlName]="param().key"
            [label]="label()"
            [required]="param().required"
            [singleCheckBox]="false"
            [options]="checkboxOptions()" />
        } @else {
          <ng-container *ngTemplateOutlet="noOptions" />
        }
      }

      @case (ParamType.TEXT) {
        <lib-input
          [inputFormControlName]="param().key"
          [id]="controlId()"
          type="text"
          [name]="param().key"
          [label]="label()"
          [required]="param().required" />
      }

      @case (ParamType.NUMBER) {
        <lib-input
          [inputFormControlName]="param().key"
          [id]="controlId()"
          type="number"
          [name]="param().key"
          [label]="label()"
          [min]="param().min ?? ''"
          [max]="param().max ?? ''"
          [required]="param().required" />
      }

      @case (ParamType.BOOL) {
        <lib-toggle
          [id]="controlId()"
          [formGroup]="group()"
          [inputFormControlName]="param().key"
          [options]="toggleOption()" />
      }

      @case (ParamType.LOCALIZED_TEXT) {
        <div [formGroupName]="param().key" class="flex flex-col gap-md">
          @if (param().placeholders) {
            <lib-dual-lang-text-area
              [inputFormControlName]="param().key"
              [id]="controlId()"
              [name]="param().key"
              [label]="label()"
              [requiredEn]="param().required"
              [requiredAr]="param().required"
              [rowsNumber]="'4'"
              [width]="''"
              [placeholderEn]="'reflection_locale_en' | transloco"
              [placeholderAr]="'reflection_locale_ar' | transloco" />
          } @else {
            <span
              class="text-sm font-medium text-neutral-900"
              [class.label-required]="param().required">
              {{ label() }}
            </span>
            <lib-dual-lang-input
              [requiredGroup]="param().required"
              [inputFormControlName]="param().key"
              [id]="controlId()"
              type="text"
              [name]="param().key"
              [requiredEn]="false"
              [requiredAr]="false"
              [placeholderEn]="'reflection_locale_en' | transloco"
              [placeholderAr]="'reflection_locale_ar' | transloco" />
          }
        </div>
        @if (param().placeholders && placeholderOptions().length) {
          <div [formGroup]="pickerForm">
            <lib-select
              inputFormControlName="token"
              [id]="controlId() + '_placeholder'"
              [name]="param().key + '_placeholder'"
              [label]="'reflection_placeholder_picker_label' | transloco"
              [placeholder]="
                'reflection_placeholder_picker_placeholder' | transloco
              "
              [options]="placeholderOptions()"
              [selectedKeyName]="'label'"
              [selectedKeyValue]="'value'"
              [hasSearch]="true"
              [searchPlaceholder]="'type_to_search_placeholder' | transloco" />
          </div>
        }
      }

      @case (ParamType.USER_LIST) {
        <lib-multi-lookup-field
          [id]="controlId()"
          [inputFormControlName]="param().key"
          [name]="param().key"
          [label]="label()"
          [placeholder]="'reflection_users_placeholder' | transloco"
          [required]="param().required"
          [apiUrl]="userLookupApi"
          [selectedOption]="selectedUsers()"
          [labelValueAssignList]="userLookupAssignList()"
          [lang]="'current_lang' | transloco"
          [searchPlaceholder]="'type_to_search_placeholder' | transloco"
          [detailsNotFoundLabel]="'details_not_found_label' | transloco"
          [addNewAssignLabel]="
            'multi_lookup_field_add_new_assign_label' | transloco
          "
          [noSearchResultLabel]="
            'user_dropdown_input_no_data_label' | transloco
          " />
      }

      @case (ParamType.RECIPIENTS) {
        <span
          class="text-sm font-medium text-neutral-900"
          [class.label-required]="param().required">
          {{ label() }}
        </span>
        @for (row of recipientRows(); track row.group) {
          <div
            [formGroup]="row.group"
            class="flex flex-col gap-md rounded-lg border border-neutral-200 p-md">
            <lib-select
              inputFormControlName="type"
              [id]="controlId() + '_type_' + row.index"
              name="type"
              [required]="true"
              [options]="recipientTypeOptions()"
              [selectedKeyName]="'label'"
              [selectedKeyValue]="'value'"
              [placeholder]="
                'reflection_recipient_type_placeholder' | transloco
              " />
            @switch (row.type) {
              @case (RecipientType.USERS) {
                <lib-multi-lookup-field
                  [id]="controlId() + '_users_' + row.index"
                  inputFormControlName="user_ids"
                  [name]="'user_ids_' + row.index"
                  [placeholder]="'reflection_users_placeholder' | transloco"
                  [required]="true"
                  [apiUrl]="userLookupApi"
                  [selectedOption]="row.selectedUsers"
                  [labelValueAssignList]="userLookupAssignList()"
                  [lang]="'current_lang' | transloco"
                  [searchPlaceholder]="'type_to_search_placeholder' | transloco"
                  [detailsNotFoundLabel]="'details_not_found_label' | transloco"
                  [addNewAssignLabel]="
                    'multi_lookup_field_add_new_assign_label' | transloco
                  "
                  [noSearchResultLabel]="
                    'user_dropdown_input_no_data_label' | transloco
                  " />
              }
              @case (RecipientType.USER_FIELD) {
                @if (userFieldOptions().length) {
                  <lib-select
                    inputFormControlName="field_key"
                    [id]="controlId() + '_field_' + row.index"
                    name="field_key"
                    [required]="true"
                    [options]="userFieldOptions()"
                    [selectedKeyName]="'label'"
                    [selectedKeyValue]="'value'"
                    [placeholder]="
                      'reflection_recipient_field_placeholder' | transloco
                    " />
                } @else {
                  <p class="text-xs text-neutral-500">
                    {{ 'reflection_param_no_options' | transloco }}
                  </p>
                }
              }
            }
            <div class="flex justify-end">
              <lib-button
                [id]="controlId() + '_remove_' + row.index"
                [label]="'reflection_remove_row' | transloco"
                [btnClass]="'py-xs px-md text-sm rounded-md text-destructive-700 bg-white font-medium border border-destructive-300'"
                (click)="removeRow(row.index)" />
            </div>
          </div>
        }
        <div>
          <lib-button
            [id]="controlId() + '_add'"
            [label]="'reflection_add_recipient' | transloco"
            [btnClass]="'py-xs px-md text-sm rounded-md text-primary-700 bg-white font-medium border border-primary-300'"
            (click)="addRow()" />
        </div>
      }

      @case (ParamType.MAP) {
        <span
          class="text-sm font-medium text-neutral-900"
          [class.label-required]="param().required">
          {{ label() }}
        </span>
        @for (row of rows(); track row; let i = $index) {
          <div class="grid grid-cols-[1fr_1fr_auto] items-end gap-md">
            <lib-reflection-param-input
              [param]="mapKeyParam()"
              [group]="row"
              [fields]="fields()"
              [placeholders]="placeholders()"
              [scope]="scope()"
              [idPrefix]="controlId() + '_' + i" />
            <lib-reflection-param-input
              [param]="mapValueParam()"
              [group]="row"
              [fields]="fields()"
              [placeholders]="placeholders()"
              [scope]="scope()"
              [idPrefix]="controlId() + '_' + i" />
            <lib-button
              [id]="controlId() + '_remove_' + i"
              [label]="'reflection_remove_row' | transloco"
              [btnClass]="'py-xs px-md text-sm rounded-md text-destructive-700 bg-white font-medium border border-destructive-300'"
              (click)="removeRow(i)" />
          </div>
        }
        <div>
          <lib-button
            [id]="controlId() + '_add'"
            [label]="'reflection_add_entry' | transloco"
            [btnClass]="'py-xs px-md text-sm rounded-md text-primary-700 bg-white font-medium border border-primary-300'"
            (click)="addRow()" />
        </div>
      }

      @default {
        <!-- url, secret and flow_template land with Tasks W8 and W10 -->
        <span class="text-sm font-medium text-neutral-900">{{ label() }}</span>
        <p class="text-xs text-neutral-500">
          {{ 'reflection_param_unsupported' | transloco }}
        </p>
      }
    }

    @if (hint()) {
      <p class="text-xs text-neutral-500">{{ hint() }}</p>
    }
    @if (showValidation()) {
      <lib-validation-message
        [lang]="'current_lang' | transloco"
        [control]="param().key" />
    }
  </div>
}

<ng-template #noOptions>
  <span class="text-sm font-medium text-neutral-900">{{ label() }}</span>
  <p class="text-xs text-neutral-500">
    {{ 'reflection_param_no_options' | transloco }}
  </p>
</ng-template>
